import React, { useState, useEffect } from 'react';
import { useDispatch } from "react-redux";

// shadcn/ui components
import { Button } from "components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Separator } from "components/ui/separator";
import { Badge } from "components/ui/badge";

// Lucide icons
import { Shuffle, Calendar, Users, CheckCircle2, Building2, FileText, X } from "lucide-react";

import { SG_MERGE_DEBRIEF_SCHEDULE } from "constants/actions";
import { format } from 'date-fns';

export default function MergeSurvey({
    debriefSchedule,
    setMergeSurvey,
    mergeSurvey
}) {
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [disAllowSecond, setDisAllowSecond] = useState(true);
    const [resetMerge, setResetMerge] = useState(true);

    function Submit() {
        setOpen(false);
        dispatch({
            type: SG_MERGE_DEBRIEF_SCHEDULE,
            payload: {
                original_brief_id: mergeSurvey?.original_brief?.id,
                merge_brief_id: mergeSurvey?.merge_brief?.id
            }
        });
    }

    useEffect(() => {
        if (mergeSurvey?.original_brief?.id && mergeSurvey?.merge_brief?.id) {
            setOpen(true);
        }
    }, [mergeSurvey]);

    function AddDebriefSchedule() {
        setMergeSurvey({
            original_brief: debriefSchedule,
            org: debriefSchedule?.organization,
            name: debriefSchedule?.name,
        });
    }

    function UpdateDebriefSchedule() {
        const _merged = mergeSurvey;
        _merged.merge_brief = debriefSchedule;
        if (_merged?.original_brief?.id && _merged?.merge_brief?.id) {
            setOpen(true);
        }
        setMergeSurvey(_merged);
    }

    function CloseMerge() {
        setOpen(false);
    }

    function ResetMerge() {
        setOpen(false);
        setMergeSurvey({
            org: null,
            name: null,
            original_brief: null,
            merge_brief: null
        });
    }

    useEffect(() => {
        if (mergeSurvey?.original_brief) {
            setResetMerge(false);
            if (debriefSchedule?.id === mergeSurvey?.original_brief?.id) {
                setResetMerge(true);
                return;
            }
            if (debriefSchedule.organization === mergeSurvey?.original_brief?.organization) {
                if (debriefSchedule.name === mergeSurvey?.original_brief?.name) {
                    setDisAllowSecond(false);
                }
            }
        }
    }, [debriefSchedule, mergeSurvey]);

    const renderSurveyDetails = (survey, title) => (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">{title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <div>
                        <p className="text-xs text-muted-foreground">ID</p>
                        <p className="text-sm font-semibold">#{survey?.id}</p>
                    </div>
                </div>
                
                <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div>
                        <p className="text-xs text-muted-foreground">Org Id</p>
                        <p className="text-sm font-semibold">#{survey?.organization}</p>
                    </div>
                </div>
                
                <div>
                    <p className="text-xs text-muted-foreground">Primary</p>
                    <p className="text-sm font-semibold">{survey?.name}</p>
                </div>
                
                <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <div>
                        <p className="text-xs text-muted-foreground">Requests</p>
                        <p className="text-sm font-semibold">{survey?.total_requests || 0}</p>
                    </div>
                </div>
                
                <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                    <div>
                        <p className="text-xs text-muted-foreground">Responses</p>
                        <p className="text-sm font-semibold">{survey?.total_responses || 0}</p>
                    </div>
                </div>
                
                <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <div>
                        <p className="text-xs text-muted-foreground">Scheduled</p>
                        <p className="text-sm font-semibold">
                            {survey?.scheduled_for 
                                ? format(new Date(survey?.scheduled_for), 'dd-MMM-yyyy hh:mm')
                                : 'N/A'}
                        </p>
                    </div>
                </div>
                
                <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <div>
                        <p className="text-xs text-muted-foreground">Close At</p>
                        <p className="text-sm font-semibold">
                            {survey?.close_at 
                                ? format(new Date(survey?.close_at), 'dd-MMM-yyyy hh:mm')
                                : 'N/A'}
                        </p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    return (
        <>
            {mergeSurvey?.original_brief?.id ? (
                resetMerge ? (
                    <Button
                        variant="outline"
                        onClick={ResetMerge}
                        className="gap-2"
                    >
                        <Shuffle className="h-4 w-4" />
                        Reset
                    </Button>
                ) : (
                    <Button
                        variant="destructive"
                        disabled={open || disAllowSecond}
                        onClick={UpdateDebriefSchedule}
                        className="gap-2"
                    >
                        <Shuffle className="h-4 w-4" />
                        Merge
                    </Button>
                )
            ) : (
                <Button
                    variant="default"
                    onClick={AddDebriefSchedule}
                    className="gap-2"
                >
                    <Shuffle className="h-4 w-4" />
                    Merge
                </Button>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-4xl">
                    <DialogHeader>
                        <DialogTitle>You are about to merge these two surveys</DialogTitle>
                        <DialogDescription>
                            Please review the details below before confirming the merge.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-4">
                        {renderSurveyDetails(mergeSurvey?.original_brief, "Original Survey")}
                        {renderSurveyDetails(mergeSurvey?.merge_brief, "Merge Survey")}
                    </div>

                    <div className="flex items-center justify-center my-4">
                        <Badge variant="outline" className="px-4 py-2">
                            And
                        </Badge>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={CloseMerge}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={Submit}
                            className="gap-2"
                        >
                            <Shuffle className="h-4 w-4" />
                            Merge Survey
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
